// ResetPasswordPage - set a new password from an emailed reset-token link.

import { Loader2 } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { useToast } from '@/context/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/classnames';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const toast = useToast();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const strength = useMemo(() => passwordStrength(pw), [pw]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (pw.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword(token, pw);
      toast.success('Password updated. Sign in again.');
      nav('/login');
    } catch {
      toast.error('Token invalid or expired');
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Missing token">
        <div className="card p-8 text-center text-sm text-ink-300">
          This link is missing its reset token.{' '}
          <Link to="/login" className="link">Sign in</Link>.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password">
      <div className="card p-7">
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 12 characters"
            />
            <StrengthMeter score={strength.score} label={strength.label} />
          </div>
          <div>
            <label className="label">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm && pw !== confirm && (
              <div className="error-text">Passwords don't match.</div>
            )}
          </div>
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Update password
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-xs text-ink-400 hover:text-accent-300 transition-colors">
            ← Back to sign in
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}

const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Strong', 'Excellent'] as const;

function passwordStrength(pw: string): { score: number; label: string } {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[^a-zA-Z0-9]/.test(pw) || /\d/.test(pw)) score++;
  if ((/[A-Z]/.test(pw) && /[a-z]/.test(pw)) || pw.length >= 24) score++;
  score = Math.min(4, score);
  return { score, label: STRENGTH_LABELS[score] };
}

function StrengthMeter({ score, label }: { score: number; label: string }) {
  if (!label) return null;
  const color =
    score <= 1 ? 'bg-verdict-malicious' : score === 2 ? 'bg-verdict-suspicious' : 'bg-verdict-clean';
  const textColor =
    score <= 1 ? 'text-verdict-malicious' : score === 2 ? 'text-verdict-suspicious' : 'text-verdict-clean';
  return (
    <div className="mt-2.5">
      <div className="flex gap-[5px]">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full',
              i < score ? color : 'bg-ink-800',
            )}
            style={i < score ? { animation: `vz-bar .4s cubic-bezier(.16,1,.3,1) ${i * 0.05}s both`, transformOrigin: 'left' } : undefined}
          />
        ))}
      </div>
      <div className={cn('text-[10.5px] font-medium mt-[7px]', textColor)}>{label}</div>
    </div>
  );
}
